import React, { useEffect, useState } from 'react';
import { stroot, organisation as organisationAPI } from '@birdi/js-sdk';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { css } from '@emotion/react';
import { BirdiTheme, buttonBlue, breakpoints, blocks } from '@birdi/theme';
import { Toggle } from '@birdi/toggle';
import BirdiLogoNavySVG from '@birdi/icons/birdi-logo-navy.svg';
import LoaderSVG from '@birdi/icons/loader.svg';
import { uploadOne, fileToUppyOpts } from '@birdi/uppy-s3';
import { useMetaSelector, updateOrganisation } from '../meta/reducer';

interface Fields {
  name: string;
  logo: string;
  emailHeader: string;
  displayBranding: boolean;
  displayLogo: boolean;
  displayEmailHeader: boolean;
}

const boxStyle = (theme: BirdiTheme) => css`
  width: 70%;
  margin: 1em;
  padding: 1em;
  border: solid 1px ${theme.mono30};
  border-radius: 5px;
  @media (max-width: ${breakpoints.xs}) {
    width: calc(100% - 5em);
  }
`;
const firstRowStyle = (theme: BirdiTheme) => css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1em;
`;
const imageRowStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1em;
`;
const uploadButtonStyle = (theme: BirdiTheme) => css`
  ${buttonBlue(theme, { baseSize: 'sm' })};
  width: 6em;
`;
const clearButtonStyle = (theme: BirdiTheme) => css`
  ${buttonBlue(theme, { baseSize: 'sm' })};
  background-color: ${theme.mono0};
  border: solid 1px ${theme.mono98};
  color: ${theme.mono98};
  width: 7em;
  &:active {
    color: ${theme.mono98};
  }
`;

const FileUpload = (props) => {
  const { imageUrl, uploading, image } = props;

  return (
    <div
      css={(theme) => css`
        position: relative;
        width: ${image === 'menuIcon' ? '40px' : '100px'};
        height: ${image === 'menuIcon' ? '40px' : '80px'};
        background-color: ${theme.mono30};
        border: solid 1px ${theme.mono50};
      `}
    >
      <div
        css={(theme: BirdiTheme) => css`
          height: 100%;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        `}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            className="photo"
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              height: 100%;
              width: 100%;
              object-fit: contain;
            `}
          />
        )}
        {!imageUrl && uploading && (
          <LoaderSVG
            css={css`
              stroke-width: 2px;
            `}
          />
        )}
        {!imageUrl && !uploading && image === 'menuIcon' && (
          <BirdiLogoNavySVG
            css={css`
              font-size: 30px;
            `}
          />
        )}
        {!imageUrl && !uploading && image === 'logo' && (
          <BirdiLogoNavySVG
            css={css`
              font-size: 80px;
            `}
          />
        )}
        {!imageUrl && !uploading && image === 'emailHeader' && (
          <BirdiLogoNavySVG
            css={css`
              font-size: 80px;
            `}
          />
        )}
      </div>
    </div>
  );
};

const CustomisationBranding = (): React.ReactElement => {
  const { orgId } = useParams<{ orgId: string }>();
  const dispatch = useDispatch();
  const meta = useMetaSelector();
  const [org, setOrg] = useState(null);

  const menuIconFileInput = React.createRef();
  const logoFileInput = React.createRef();
  const emailHeaderFileInput = React.createRef();

  const [uploadingMenuIcon, setUploadingMenuIcon] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingEmailHeader, setUploadingEmailHeader] = useState(false);

  const currentOrg = meta.organisation.id === orgId;

  useEffect(() => {
    if (!orgId) return;
    organisationAPI.getOneById(stroot('iy3ohb'), orgId).then((res) => {
      setOrg(res.body);
    });
  }, [orgId]);

  const updateFields = async (fieldsToUpdate: Fields) => {
    if (!orgId) return;
    const updatedOrg = await organisationAPI.updateBranding(
      stroot('iy3ohb'),
      orgId,
      fieldsToUpdate,
    );
    setOrg({ ...org, ...fieldsToUpdate });
    if (currentOrg) dispatch(updateOrganisation(fieldsToUpdate));
  };

  const uploadMenuIcon = async () => {
    const file = menuIconFileInput.current.files[0];
    setUploadingMenuIcon(true);
    const upload = await uploadOne({
      ...fileToUppyOpts(file),
      public: true,
    });
    await updateFields({ menuIcon: upload.uploadURL });
    setUploadingMenuIcon(false);
  };
  const clearMenuIcon = async () => {
    if (
      window.confirm('Are you sure you would like to remove the menu icon?')
    ) {
      const fields = {
        menuIcon: null,
      };
      await updateFields(fields);
    }
  };

  const uploadLogo = async () => {
    const file = logoFileInput.current.files[0];
    setUploadingLogo(true);
    const upload = await uploadOne({
      ...fileToUppyOpts(file),
      public: true,
    });
    await updateFields({ logo: upload.uploadURL });
    setUploadingLogo(false);
  };
  const clearLogo = async () => {
    if (window.confirm('Are you sure you would like to remove the logo?')) {
      const fields = {
        logo: null,
        displayLogo: false,
      };
      await updateFields(fields);
    }
  };

  const uploadEmailHeader = async () => {
    const file = emailHeaderFileInput.current.files[0];
    setUploadingEmailHeader(true);
    const upload = await uploadOne({
      ...fileToUppyOpts(file),
      public: true,
    });
    await updateFields({ emailHeader: upload.uploadURL });
    setUploadingEmailHeader(false);
  };
  const clearEmailHeader = async () => {
    if (
      window.confirm('Are you sure you would like to remove the email header?')
    ) {
      const fields = {
        emailHeader: null,
        displayEmailHeader: false,
      };
      await updateFields(fields);
    }
  };

  if (org === null)
    return (
      <p
        css={css`
          margin: 2em;
        `}
      >
        Loading ...
      </p>
    );

  return (
    <div>
      <div css={blocks.pageTitleHolder}>
        <h2 css={blocks.pageTitle}>
          Customization &nbsp;&nbsp;
          <span
            css={(theme) => css`
              color: ${theme.mono40};
            `}
          >
            {org?.name}
          </span>
        </h2>
      </div>
      <div css={blocks.pageGraySection}>
        <p>Manage your workspace branding.</p>
      </div>
      <div css={boxStyle}>
        <div css={firstRowStyle}>
          <div>Display workspace name instead of logo.</div>
          <Toggle
            checked={org.displayBranding}
            onChange={(check: boolean) =>
              updateFields({ displayBranding: check })
            }
          />
        </div>
      </div>
      <div css={boxStyle}>
        <div css={firstRowStyle}>
          <div
            css={css`
              font-weight: bold;
            `}
          >
            Main menu icon
          </div>
        </div>
        <div>
          <p
            css={(theme) => css`
              color: ${theme.mono50};
            `}
          >
            Recommended resolution: 40x40px
          </p>
          <div css={imageRowStyle}>
            <FileUpload
              imageUrl={org?.menuIcon}
              uploading={uploadingMenuIcon}
              image="menuIcon"
            />
            <label htmlFor="upload-menu-icon" css={uploadButtonStyle}>
              Upload
              <input
                id="upload-menu-icon"
                type="file"
                accept="image/*"
                onChange={uploadMenuIcon}
                ref={menuIconFileInput}
                hidden
              />
            </label>
            <button
              type="button"
              css={clearButtonStyle}
              onClick={clearMenuIcon}
              disabled={!org.menuIcon}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
      <div css={boxStyle}>
        <div css={firstRowStyle}>
          <div
            css={css`
              font-weight: bold;
            `}
          >
            Logo
          </div>
          <Toggle
            checked={org?.displayLogo}
            onChange={(check: boolean) => updateFields({ displayLogo: check })}
            disabled={!org.logo}
          />
        </div>
        <div>
          <p
            css={(theme) => css`
              color: ${theme.mono50};
            `}
          >
            Recommended: Dark font, 400x320px PNG with a transparent background
          </p>
          <div css={imageRowStyle}>
            <FileUpload
              imageUrl={org?.logo}
              uploading={uploadingLogo}
              image="logo"
            />
            <label htmlFor="upload-logo" css={uploadButtonStyle}>
              Upload
              <input
                id="upload-logo"
                type="file"
                accept="image/*"
                onChange={uploadLogo}
                ref={logoFileInput}
                hidden
              />
            </label>
            <button
              type="button"
              css={clearButtonStyle}
              onClick={clearLogo}
              disabled={!org.logo}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
      <div css={boxStyle}>
        <div css={firstRowStyle}>
          <div
            css={css`
              font-weight: bold;
            `}
          >
            Email header
          </div>
          <Toggle
            checked={org.displayEmailHeader}
            onChange={(check: boolean) =>
              updateFields({ displayEmailHeader: check })
            }
            disabled={!org.emailHeader}
          />
        </div>
        <div>
          <p
            css={(theme) => css`
              color: ${theme.mono50};
            `}
          >
            Recommended resolution: Dark font, 400x320px
          </p>
          <div css={imageRowStyle}>
            <FileUpload
              imageUrl={org?.emailHeader}
              uploading={uploadingEmailHeader}
              image="emailHeader"
            />
            <label htmlFor="upload-email-header" css={uploadButtonStyle}>
              Upload
              <input
                id="upload-email-header"
                type="file"
                accept="image/*"
                onChange={uploadEmailHeader}
                ref={emailHeaderFileInput}
                hidden
              />
            </label>
            <button
              type="button"
              css={clearButtonStyle}
              onClick={clearEmailHeader}
              disabled={!org.emailHeader}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomisationBranding;
